import { defineStore } from 'pinia';
import {
  JOBTYPE_COLLECTION,
  JOBTYPE_PICKUP,
  LAST_UPDATE_KEY,
  TRACKING_DELIVERY_STATE_CLOSE,
  TRACKING_DELIVERY_STATE_FAR,
  TRACKING_DELIVERY_STATE_LAST,
  TRACKING_DELIVERY_STATE_NEAR,
  TRACKING_DELIVERY_STATE_PRE_ETA,
  TRACKING_DELIVERY_STATE_UNCLEAR,
  TRACKING_STATE_BLOCKED,
  TRACKING_STATE_COLLECTED,
  TRACKING_STATE_DELIVERED,
  TRACKING_STATE_ETA_VALID_DELTA,
  TRACKING_STATE_NOT_COLLECTED,
  TRACKING_STATE_NOT_DELIVERED,
  TRACKING_STATE_NOT_PICKED_UP,
  TRACKING_STATE_OUT_FOR_DELIVERY,
  TRACKING_STATE_PICKED_UP,
  TRACKING_STATE_SHADOWED,
  TRACKING_STATE_THRESHOLD_BLURRY,
  TRACKING_STATE_THRESHOLD_MED,
  TRACKING_STATE_THRESHOLD_MIN,
  TRACKING_STATE_UNKNOWN
} from '@/stores/constants.js';
import { configGetter } from '@/config/configGetter.js';
import { useAlertStore } from '@/stores/alert.js';
import { fetchWithBaseParams } from '@/util/fetch.js';
import { useWebsocketStore } from '@/stores/websocket.js';
import { useCustomizedDeliveryStore } from '@/stores/customizedDelivery.js';
import { sendAnalytics } from '@/services/analytics.js';
import moment from 'moment-timezone';
import { useRatingStore } from '@/stores/rating.js';
import intersection from '@/util/intersection.js';

const getEtaTimestampDelta = timestamp => {
  if (!timestamp) {
    return 0;
  }
  const now = Date.now();
  return new Date(timestamp).getTime() - now;
};

const getMinutesLeft = (etaTimestampMin, etaTimestampMax, now = moment()) => {
  let response = {
    from: null,
    to: null,
    format: null
  };

  if (!etaTimestampMin || !etaTimestampMax) return response;

  const from = moment(etaTimestampMin);
  const to = moment(etaTimestampMax);
  const durationStart = moment.duration(from.diff(now));
  const durationEnd = moment.duration(to.diff(now));

  if (durationStart.asMinutes() > 0 && durationStart.asMinutes() <= 60) {
    response = {
      from: Math.ceil(durationStart.asMinutes()),
      to: Math.ceil(durationEnd.asMinutes()),
      format: 'min'
    };
  }
  if (durationStart.asMinutes() > 60) {
    response = {
      from: moment.utc(durationStart.asMilliseconds()).format('h:mm'),
      to: moment.utc(durationEnd.asMilliseconds()).format('h:mm'),
      format: 'h'
    };
  }
  return response;
};

/**
 * @typedef {('SUCCESS'|'LOADING'|'ERROR')} RequestState
 */

/**
 * @typedef {Object} ErrorPayload
 * @property {number} statusCode - The http status code of the error response
 * @property {string} id - The error identifier code (e.g. 'PARCEL-404')
 * @property {string|null} reason - A human-readable description of the error (e.g. 'The provided trackingId cannot be found.')
 */
export const useTrackingDataStore = defineStore('trackingData', {
  state: () => ({
    isPrivate: false,
    data: {
      supportedCustomizedDeliveryTypes: [],
      status: {
        remainingStops: 0
      },
      maxStopsToShowDriverLocation: 10
    },
    routeJumpCount: 0,
    refreshingMinDelta: 30000,
    /** @type {RequestState | undefined} */
    requestState: undefined,
    /** @type {ErrorPayload | undefined} */
    errorPayload: undefined
  }),
  getters: {
    supportedCustomizedDeliveryTypes: state => {
      return intersection(state.data.supportedCustomizedDeliveryTypes, [
        'DELIVER_TO_NEIGHBOR',
        'DEPOSIT_PERMISSION'
      ]);
    },
    isServiceTypePickup(state) {
      return state.data.jobType === JOBTYPE_PICKUP;
    },
    isServiceTypeCollection(state) {
      return state.data.jobType === JOBTYPE_COLLECTION;
    },
    getTrackingState() {
      const tracking = { ...this.data.status };
      const TRACKING_STATE_THRESHOLD_MED =
        this.data.maxStopsToShowDriverLocation;
      const trackingType = tracking.type;
      const stops =
        typeof tracking.remainingStops === 'number'
          ? tracking.remainingStops
          : undefined;
      const delivered = trackingType === TRACKING_STATE_DELIVERED;
      const pickedUp = trackingType === TRACKING_STATE_PICKED_UP;
      const notPickedUp = trackingType === TRACKING_STATE_NOT_PICKED_UP;
      const collected = trackingType === TRACKING_STATE_COLLECTED;
      const notCollected = trackingType === TRACKING_STATE_NOT_COLLECTED;
      const outForDelivery = trackingType === TRACKING_STATE_OUT_FOR_DELIVERY;
      const notDelivered = trackingType === TRACKING_STATE_NOT_DELIVERED;
      const unknown = trackingType === TRACKING_STATE_UNKNOWN;
      const blocked = trackingType === TRACKING_STATE_BLOCKED;
      const shadowed = this.data.tourMode === TRACKING_STATE_SHADOWED;
      const negativeStopsExceeded =
        Math.sign(stops) === -1 && stops < TRACKING_STATE_THRESHOLD_BLURRY;
      const moreThan15MinAway =
        getEtaTimestampDelta(tracking.etaTimestamp) >
        -TRACKING_STATE_ETA_VALID_DELTA;

      if (this.isServiceTypePickup) {
        if (pickedUp) {
          return TRACKING_STATE_PICKED_UP;
        }

        if (notPickedUp) {
          return TRACKING_STATE_NOT_PICKED_UP;
        }
      }

      if (this.isServiceTypeCollection) {
        if (collected) {
          return TRACKING_STATE_COLLECTED;
        }

        if (notCollected) {
          return TRACKING_STATE_NOT_COLLECTED;
        }
      }

      if (notDelivered) {
        return TRACKING_STATE_NOT_DELIVERED;
      }

      if (delivered) {
        return TRACKING_STATE_DELIVERED;
      }

      if (blocked) {
        sendAnalytics(this.data, 'tracking_state', 'blocked', {
          reason: 'blocked status from backend'
        });
        return TRACKING_STATE_BLOCKED;
      }

      if (outForDelivery) {
        if (tracking.isPreEta && tracking.etaTimestamp !== null) {
          return TRACKING_DELIVERY_STATE_PRE_ETA;
        }

        if (shadowed) {
          return TRACKING_STATE_SHADOWED;
        }

        if (
          stops < this.data.trackingStateThresholdMax &&
          stops > TRACKING_STATE_THRESHOLD_MED
        ) {
          return TRACKING_DELIVERY_STATE_NEAR;
        }
        if (
          stops <= TRACKING_STATE_THRESHOLD_MED &&
          (stops > TRACKING_STATE_THRESHOLD_MIN || moreThan15MinAway)
        ) {
          return TRACKING_DELIVERY_STATE_CLOSE;
        }
        if (
          stops <= TRACKING_STATE_THRESHOLD_MIN &&
          stops >= TRACKING_STATE_THRESHOLD_BLURRY
        ) {
          return TRACKING_DELIVERY_STATE_LAST;
        }
        if (stops >= this.data.trackingStateThresholdMax) {
          return TRACKING_DELIVERY_STATE_FAR;
        }
        if (negativeStopsExceeded) {
          return TRACKING_DELIVERY_STATE_UNCLEAR;
        }
        if (tracking.etaTimestamp === null) {
          return TRACKING_DELIVERY_STATE_UNCLEAR;
        }
      }

      if (unknown) {
        sendAnalytics(this.data, 'tracking_state', 'unknown', {
          reason: 'unknown status from backend'
        });
        return TRACKING_STATE_UNKNOWN;
      }

      return TRACKING_DELIVERY_STATE_UNCLEAR;
    },

    getTimeframe(state) {
      const { etaTimestampMin: from, etaTimestampMax: to } =
        state.data?.status || {};
      if (!from || !to) return false;

      return { from, to };
    },

    getDeliveryStatusDetail(state) {
      return state.data.status?.detail || null;
    },

    showPositionExplanation() {
      return !!(
        (this.getTrackingState === TRACKING_DELIVERY_STATE_NEAR ||
          this.getTrackingState === TRACKING_DELIVERY_STATE_FAR ||
          (this.getTrackingState === TRACKING_STATE_SHADOWED &&
            this.data.status.remainingStops >
              this.data.maxStopsToShowDriverLocation)) &&
        this.consigneeAddress
      );
    },

    getMinutesLeft(state) {
      const { etaTimestampMin, etaTimestampMax } = state.data?.status || {};
      return getMinutesLeft(etaTimestampMin, etaTimestampMax);
    },

    getMinutesLeftAt(state) {
      const { etaTimestampMin, etaTimestampMax } = state.data?.status || {};

      return now =>
        getMinutesLeft(etaTimestampMin, etaTimestampMax, moment(now));
    },
    isJobFinished() {
      return [
        TRACKING_STATE_DELIVERED,
        TRACKING_STATE_NOT_DELIVERED,
        TRACKING_STATE_NOT_PICKED_UP,
        TRACKING_STATE_PICKED_UP,
        TRACKING_STATE_COLLECTED,
        TRACKING_STATE_NOT_COLLECTED
      ].includes(this.getTrackingState);
    },
    isJobInProgress() {
      return [
        TRACKING_STATE_OUT_FOR_DELIVERY,
        TRACKING_STATE_UNKNOWN,
        TRACKING_STATE_BLOCKED,
        TRACKING_STATE_SHADOWED,
        TRACKING_DELIVERY_STATE_PRE_ETA,
        TRACKING_DELIVERY_STATE_NEAR,
        TRACKING_DELIVERY_STATE_CLOSE,
        TRACKING_DELIVERY_STATE_LAST,
        TRACKING_DELIVERY_STATE_FAR,
        TRACKING_DELIVERY_STATE_UNCLEAR
      ].includes(this.getTrackingState);
    },
    isJobStatusPickedUp() {
      return this.getTrackingState === TRACKING_STATE_PICKED_UP;
    },
    isJobStatusNotPickedUp() {
      return this.getTrackingState === TRACKING_STATE_NOT_PICKED_UP;
    },
    isJobStatusCollected() {
      return this.getTrackingState === TRACKING_STATE_COLLECTED;
    },
    isJobStatusNotCollected() {
      return this.getTrackingState === TRACKING_STATE_NOT_COLLECTED;
    },
    isJobStatusDelivered() {
      return this.getTrackingState === TRACKING_STATE_DELIVERED;
    },
    isJobStatusNotDelivered() {
      return this.getTrackingState === TRACKING_STATE_NOT_DELIVERED;
    },
    isJobStatusShadowed() {
      return this.getTrackingState === TRACKING_STATE_SHADOWED;
    },
    isJobStatusBlocked() {
      return this.getTrackingState === TRACKING_STATE_BLOCKED;
    },
    isJobStatusUnknown() {
      return this.getTrackingState === TRACKING_STATE_UNKNOWN;
    },
    isJobStatusUnclear() {
      return this.getTrackingState === TRACKING_DELIVERY_STATE_UNCLEAR;
    },
    isJobDeliveryStatusPreEta() {
      return this.getTrackingState === TRACKING_DELIVERY_STATE_PRE_ETA;
    },
    isServiceTypeInbound() {
      return this.isServiceTypePickup || this.isServiceTypeCollection;
    },
    isJobStatusInboundSuccess() {
      return this.isJobStatusPickedUp || this.isJobStatusCollected;
    },
    isJobStatusInboundFail() {
      return this.isJobStatusNotPickedUp || this.isJobStatusNotCollected;
    },
    consigneeAddress(state) {
      return this.isServiceTypeInbound
        ? state.data.fromAddress
        : state.data.toAddress;
    },
    hasFeedback(state) {
      return !!state.data.feedback;
    },
    feedbackChannel(state) {
      return state.data.feedback?.channel ?? null;
    },
    feedbackExternalUrl(state) {
      return state.data.feedback?.externalUrl ?? null;
    }
  },
  actions: {
    resetRequestState() {
      this.requestState = undefined;
    },
    updateTrackingData(payload, isPrivate) {
      const customizedDeliveryStore = useCustomizedDeliveryStore();
      const ratingStore = useRatingStore();

      // first reset all related stores data
      customizedDeliveryStore.setCustomizedDelivery(null);
      ratingStore.setRating(null);

      if (isPrivate) {
        this.data = {
          ...payload,
          updateTimestamp: new Date().getTime(),
          lastUpdateSource: 'rest'
        };
      } else {
        this.data = {
          ...payload,
          // 'maxStopsToShowDriverLocation' is only part of the private parcel response
          maxStopsToShowDriverLocation: TRACKING_STATE_THRESHOLD_MED,
          updateTimestamp: new Date().getTime(),
          lastUpdateSource: 'rest'
        };
      }

      if (payload.customizedDelivery) {
        customizedDeliveryStore.setCustomizedDelivery(
          payload.customizedDelivery
        );
      }

      if (payload.rating) {
        ratingStore.setRating(payload.rating);
      }

      this.isPrivate = isPrivate;
    },

    updateParcelStatus(payload) {
      this.data.status = payload.parcelStatus;
      this.data.legs = payload.legs;
      this.data.routeJumped = payload.routeJumped === true;
      if (payload.routeJumped === true) {
        this.routeJumpCount += 1;
      }
      this.data.lastUpdateSource = 'ws';
    },

    updateDriverPosition(payload) {
      if (this.isJobInProgress) {
        this.data.driverPosition = payload.coordinates;
        this.data.legs = payload.legs;
        this.data.lastUpdateSource = 'ws';
      }
    },

    async updateTrackingWithTitleAndSessionUpdate(payload, override, t) {
      if (this.shouldRefreshTrackingData() || override === true) {
        const initialTitle = document.title;
        document.title = t('overview.updatingTitle');
        window.sessionStorage.setItem(LAST_UPDATE_KEY, Date.now());

        this.reloadTrackingData(payload).finally(() => {
          document.title = initialTitle;
        });
      }
    },

    shouldRefreshTrackingData(skipMinDelta = false) {
      if (
        document.visibilityState !== 'visible' ||
        this.requestState === 'LOADING'
      ) {
        return false;
      }
      if (skipMinDelta) {
        return true;
      }
      const lastUpdate =
        parseInt(window.sessionStorage.getItem(LAST_UPDATE_KEY)) || 0;

      return Date.now() - lastUpdate > this.refreshingMinDelta;
    },

    async reloadTrackingData(payload) {
      if (payload.postcode) {
        await this.loadAdditionalTrackingData(payload);
      } else {
        await this.loadTrackingDataById(payload);
      }
    },

    async loadTrackingDataById(payload) {
      const url = `${configGetter().API_URL}/tenant/{tenant}/parcel/{trackingId}`;

      const alertStore = useAlertStore();
      const webSocketStore = useWebsocketStore();
      try {
        alertStore.clearAlert();
        this.requestState = 'LOADING';
        this.errorPayload = undefined;

        const response = await fetchWithBaseParams(url, 'GET', payload);

        this.updateTrackingData(response, false);
        webSocketStore.setIsUpdateAvailable(false);
        webSocketStore.initializeUpdates(false);
        this.requestState = 'SUCCESS';
      } catch (e) {
        this.handleApiException(e);
      }
    },

    handleApiException(e) {
      const alertStore = useAlertStore();
      const webSocketStore = useWebsocketStore();

      const msg = e.body || { id: 'generic' };
      alertStore.setAlert(msg, 'error', false);
      webSocketStore.setSubscriptionActive(false);
      this.requestState = 'ERROR';
      this.errorPayload = {
        statusCode: e.status,
        id: e?.body?.id,
        reason: e?.body?.reason
      };
    },

    async loadAdditionalTrackingData(payload) {
      const url = `${configGetter().API_URL}/tenant/{tenant}/parcel/{trackingId}/{postcode}`;

      const alertStore = useAlertStore();
      const webSocketStore = useWebsocketStore();

      try {
        alertStore.clearAlert();
        this.requestState = 'LOADING';
        this.errorPayload = undefined;

        const response = await fetchWithBaseParams(url, 'GET', payload);

        this.updateTrackingData(response, true);

        webSocketStore.setIsUpdateAvailable(false);
        webSocketStore.initializeUpdates(true);
        this.requestState = 'SUCCESS';
      } catch (e) {
        this.handleApiException(e);
      }
    },
    async triggerTrackingUrl(payload) {
      const url = payload.url;
      const alertStore = useAlertStore();

      try {
        alertStore.clearAlert();
        await fetchWithBaseParams(url, 'GET', payload);
      } catch (e) {
        const msg = e.body || { id: 'generic' };

        alertStore.setAlert(msg, 'error', false);
      }
    },
    resetTrackingData() {
      const payload = {
        supportedCustomizedDeliveryTypes: [],
        status: {
          remainingStops: 0
        },
        maxStopsToShowDriverLocation: 10
      };

      this.updateTrackingData(payload);
    }
  }
});
